/* eslint-disable max-lines */
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import {
  Box,
  Chip,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { capitalize, find, includes, isBoolean, isEmpty, isNil, map, toLower } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import LoadingModule from 'common/components/LoadingModule';
import PackSizeText from 'common/components/PackSizeText';
import formatDrugLabel from 'common/utils/formatDrugLabel';
import NothingFound from 'common/components/NothingFound';
import ReshippingIcon from 'common/components/ReshippingIcon';
import ShipByDateCell from 'common/components/ShipByDateCell';
import OrderFillType from 'common/constants/orderFillType';
import status from 'common/constants/orderStatus';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { casingFormat, formatShipByDate, useGetPartnerAccountName } from 'common/utils';
import { SortKey, SortOrder, SortState } from 'enums/common.enum';
import { orderSource } from 'enums/order.enum';
import { selectLockedItemsUserMapById } from 'store/slices/lockedLineItemsSlice';
import { updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import {
  listOrderLineItemsByPage,
  sortOrderLineItemsWorkQueue,
} from 'store/thunks/workQueueThunks';
import {
  FacilityCheckBox,
  SelectAllFacilityCheckBox,
  useTransferFacilityContext,
} from './MultiTransferFacilityButton';

const headCells = [
  {
    id: SortKey.ORDER_ID,
    label: 'Order Id',
  },
  {
    id: SortKey.PATIENT_NAME,
    label: 'Patient',
  },
  {
    id: SortKey.ITEM_NAME,
    label: 'Drug Name',
  },
  // POM-6124: dispensed NDC, sourced from the active override record like the Bulk queue.
  // Not sortable — the value comes from a joined override row, not an order column.
  {
    id: 'ndc',
    label: 'NDC',
  },
  {
    id: 'isOtc',
    label: 'Type',
  },
  {
    id: SortKey.PARTNER_NAME,
    label: 'Partner',
  },
  {
    id: SortKey.PARTNER_ACCOUNT,
    label: 'Partner Account',
  },
  {
    id: SortKey.FILL_TYPE,
    label: 'Fill Type',
  },
  {
    id: 'shippingOption',
    label: 'Shipping',
  },
  {
    id: SortKey.REQUESTED_DATE,
    label: 'Requested Date',
  },
  {
    id: SortKey.SHIP_BY_DATE,
    label: 'Ship By Date',
  },
  {
    id: SortKey.NEEDS_BY_DATE,
    label: 'Needs By Date',
  },
  {
    id: SortKey.ORDER_STATUS,
    label: 'Status',
  },
  {
    id: 'source',
    label: 'Source',
  },
  {
    id: 'Facility',
    label: 'Facility',
  },
];

const nonSortableFields = ['shippingOption', 'source', 'Facility', 'isOtc', 'ndc'];

const OrderLineItemsTable = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clearTransferItems } = useTransferFacilityContext();

  const { sortBy, pages, currentPage } = useSelector(({ workQueue }) => workQueue.orderLineItems);
  const shippingOptions = useSelector(({ systemConfigs }) => systemConfigs.shippingOptions);
  const orderLineItems = pages[currentPage];
  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const currentUserId = useSelector(({ auth }) => auth?.userId);
  const facilities = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);

  const lockedItemsUserMap = useSelector((state) =>
    selectLockedItemsUserMapById(state, currentUserId)
  );

  const allLineItems =
    orderLineItems
      ?.filter(
        ({ orderStatus, lineItemId }) =>
          !!lineItemId &&
          includes(['OPEN', 'ORDER_VERIFICATION', 'HOLD', 'ORDER_RE_VERIFICATION'], orderStatus)
      )
      .filter(({ lineItemId }) => !lockedItemsUserMap[lineItemId])
      .map(({ lineItemId, orderId, itemName, rxNumber, processingState }) => ({
        lineItemId,
        orderId,
        itemName,
        rxNumber,
        processingState,
      })) || [];

  const partnerAccountHandler = useGetPartnerAccountName();

  const getPartnerAccountName = (partnerAccountId) => {
    return partnerAccountId && partnerAccountHandler[partnerAccountId]
      ? capitalize(partnerAccountHandler[partnerAccountId].partnerAccountName)
      : 'N/A';
  };

  const totalPages = useSelector(({ workQueue }) => Math.ceil(workQueue.orderLineItems.count / 25));

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    clearTransferItems();
    dispatch(
      sortOrderLineItemsWorkQueue({
        sortBy: {
          sortKey,
          sortOrder:
            sortBy.sortKey === sortKey && sortBy.sortOrder === SortOrder.ASCENDING
              ? SortOrder.DESCENDING
              : SortOrder.ASCENDING,
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      // page didn't change, don't do anything.
      return;
    }

    clearTransferItems();
    dispatch(listOrderLineItemsByPage({ page }));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(
      updateBatchShipmentFilter({
        filter: { orderId },
      })
    );
    navigate('/batchShipments');
  };

  return (
    <Grid container direction='column'>
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    style={
                      headCell.id === SortKey.FILL_TYPE
                        ? { position: 'relative', left: '11px' }
                        : {}
                    }
                  >
                    {includes(nonSortableFields, headCell.id) ? (
                      headCell.label
                    ) : (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        onClick={handleSort}
                        data-head-property={headCell.id}
                      >
                        {headCell.label}
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? SortState.SORTED_DESCENDING
                              : SortState.SORTED_ASCENDING}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  <SelectAllFacilityCheckBox
                    allLineItems={allLineItems}
                    isDisabled={isEmpty(allLineItems)}
                  />
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {map(
                orderLineItems,
                ({
                  mpi,
                  patientName,
                  partnerName,
                  fulfillingPharmacyId,
                  orderStatus,
                  ...order
                }) => {
                  const fillingFacility = find(
                    facilities,
                    (facility) => facility.fulfillingPharmacyId === fulfillingPharmacyId
                  );

                  const facilityName = fillingFacility?.name;
                  const facilityState = fillingFacility?.state;

                  const facilityCheckBoxDisabled = !(
                    orderStatus === 'OPEN' ||
                    orderStatus === 'ORDER_VERIFICATION' ||
                    orderStatus === 'HOLD' ||
                    orderStatus === 'ORDER_RE_VERIFICATION'
                  );

                  const isLockedByOtherUser = lockedItemsUserMap[order.lineItemId];

                  return (
                    <TableRow key={order.lineItemId} hover>
                      <TableCell>{order.orderId}</TableCell>
                      <TableCell component='th' scope='row' align='left'>
                        <Link to={`/patients/${mpi}`} style={{ color: theme.palette.text.primary }}>
                          {patientName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {formatDrugLabel(order)}
                        {/* Secondary line under the drug name, identical to the Bulk queue:
                            "pack size: 1ML (vial)". Renders the no-mapping note instead when the
                            item has no resolved catalog product. */}
                        <PackSizeText
                          packSizeValue={order.packSizeValue}
                          packSizeUnit={order.packSizeUnit}
                          formAlias={order.formAlias}
                          noMappingMessage='No mappings here'
                        />
                      </TableCell>
                      <TableCell>{order.ndc || '-'}</TableCell>
                      <TableCell align='left'>
                        {isBoolean(order.isOtc) && order.isOtc ? 'OTC' : 'RX'}
                      </TableCell>
                      <TableCell>{casingFormat(partnerName)}</TableCell>
                      <TableCell>{getPartnerAccountName(order.partnerAccountId)}</TableCell>
                      <TableCell>
                        <Chip
                          sx={{
                            background:
                              order.fillType === 'REFILL'
                                ? theme.palette.info.dark
                                : theme.palette.success.dark,
                            color: theme.palette.primary.contrastText,
                            height: 35,
                            width: '70px',
                          }}
                          label={
                            <Typography sx={{ fontSize: '1.1em' }}>
                              {OrderFillType[order.fillType]}
                            </Typography>
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {order.shippingOption
                          ? find(
                              shippingOptions,
                              (option) => option.optionCode === order.shippingOption
                            )?.shippingOption || order.shippingOption
                          : 'NA'}
                      </TableCell>
                      <TableCell>
                        {format(utcToZonedTime(new Date(order.requestedDate), 'utc'), 'MM/dd/yyyy')}
                      </TableCell>
                      <TableCell>
                        <ShipByDateCell shipByDate={order.shipByDate} tags={order.tags} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.disabled' }}>
                        <Tooltip title='Legacy field — see Ship By Date' arrow>
                          <span>{formatShipByDate(order.needsByDate)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{status[orderStatus]}</TableCell>
                      <TableCell>
                        <Tooltip
                          title={orderSource[order.source]?.sourceValue || ''}
                          arrow
                          placement='top'
                        >
                          {orderSource[order.source]?.sourceKey || 'NA'}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {facilityName} - {facilityState}
                      </TableCell>
                      <TableCell>
                        <FacilityCheckBox
                          lineItem={{
                            lineItemId: order.lineItemId,
                            orderId: order.orderId,
                            itemName: order.itemName,
                            rxNumber: order.rxNumber,
                            processingState: order.processingState,
                          }}
                          isDisabled={facilityCheckBoxDisabled || isLockedByOtherUser}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            order.inBatchShipmentQueue
                              ? batchShippingTooltip({
                                  isBusinessAddress: order.isBusinessAddress,
                                  isClickable: true,
                                })
                              : ''
                          }
                          arrow
                          placement='top'
                        >
                          {order.inBatchShipmentQueue ? (
                            <BatchShippingIcon
                              onClick={() => navigateToBatchShipmentQueue(order.orderId)}
                              color={perfectRxTheme.palette.warning.warningColour}
                              showBadge={order.isBusinessAddress}
                            />
                          ) : (
                            <Grid>{order.isBusinessAddress && <BusinessAddressIndicator />}</Grid>
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {order.isReshipment ? (
                          <Tooltip
                            title='Item being processed as a reshipment'
                            arrow
                            placement='top'
                            sx={{ mt: 2 }}
                          >
                            <span>
                              <ReshippingIcon />
                            </span>
                          </Tooltip>
                        ) : (
                          <Grid />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>

            {!isNil(orderLineItems) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={18} align='left' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(orderLineItems) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(orderLineItems) && isEmpty(orderLineItems) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default OrderLineItemsTable;
